import { useEffect, useState } from 'react';

/**
 * Canonical admin responsive boundaries — the single source every
 * mobile-layout change reads (audit 2026-10-17; rules and audit list in
 * docs/conventions.md § Responsive layout). The values ARE Tailwind's
 * `md`/`lg` screens, pinned in tailwind.config.js: below 768px is the
 * mobile layout, 768–1023px the intermediate one, 1024px and up desktop.
 */
export const viewportBreakpoints = {
  mobileMaxWidth: '767.98px',
  tabletMinWidth: '768px',
  belowDesktopMaxWidth: '1023.98px',
  desktopMinWidth: '1024px',
} as const;

// 767.98 rather than 767 keeps fractional viewport widths (browser zoom,
// device pixel ratios) between 767 and 768 from falling into a gap with the
// `md:` min-width(768px) classes.
export const mobileViewportQuery = `(max-width: ${viewportBreakpoints.mobileMaxWidth})`;

// The lg-side twin of that gap guard, for switches that restack at the desktop
// boundary (table→cards) instead of the mobile one.
export const belowDesktopViewportQuery = `(max-width: ${viewportBreakpoints.belowDesktopMaxWidth})`;

/** True while the viewport is in the mobile zone (below 768px). */
export function useMobileViewport(): boolean {
  const [isMobileViewport, setIsMobileViewport] = useState(
    () => window.matchMedia(mobileViewportQuery).matches
  );

  useEffect(() => {
    const mediaQueryList = window.matchMedia(mobileViewportQuery);
    const onChange = (event: MediaQueryListEvent) => setIsMobileViewport(event.matches);
    setIsMobileViewport(mediaQueryList.matches);
    mediaQueryList.addEventListener('change', onChange);
    return () => mediaQueryList.removeEventListener('change', onChange);
  }, []);

  return isMobileViewport;
}

/**
 * True while the viewport is below the desktop zone (under 1024px) — mobile
 * and intermediate together. The table→cards restack on list screens switches
 * here, not at the mobile boundary: at 768–1023px the content column (sidebar
 * plus page padding) is still too narrow for the nowrap tables.
 */
export function useBelowDesktopViewport(): boolean {
  const [isBelowDesktopViewport, setIsBelowDesktopViewport] = useState(
    () => window.matchMedia(belowDesktopViewportQuery).matches
  );

  useEffect(() => {
    const mediaQueryList = window.matchMedia(belowDesktopViewportQuery);
    const onChange = (event: MediaQueryListEvent) => setIsBelowDesktopViewport(event.matches);
    setIsBelowDesktopViewport(mediaQueryList.matches);
    mediaQueryList.addEventListener('change', onChange);
    return () => mediaQueryList.removeEventListener('change', onChange);
  }, []);

  return isBelowDesktopViewport;
}
